import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { EventqDatetimePicker } from '../datetime-picker/datetime-picker';
import {
  EventqDialogPopover,
  EventqDialogPopoverPlacement,
} from '../dialog-popover/dialog-popover';
import { EventqSelect } from '../select/select';
import type { EventqSelectMenuPlacement } from '../select/select.types';
import {
  DisarmSiteConfirm,
  SITE_DISARM_DATE_TIME_DEFAULT_OFFSET_HOURS,
  SITE_DISARM_DATE_TIME_MAX_HOURS,
  SITE_DISARM_FIXED_DURATION_OPTIONS,
  SITE_DISARM_FOR_OPTIONS,
  SiteDisarmFixedDurationMinutes,
  SiteDisarmForOption,
  SiteDisarmReasonOption,
  createDefaultSiteDisarmUntilAt,
  parseCustomDisarmDurationMinutes,
} from './disarm-site-popover.types';

export type {
  DisarmSiteConfirm,
  SiteDisarmFixedDurationMinutes,
  SiteDisarmFixedDurationOption,
  SiteDisarmForOption,
  SiteDisarmReasonOption,
} from './disarm-site-popover.types';
export {
  SITE_DISARM_DATE_TIME_DEFAULT_OFFSET_HOURS,
  SITE_DISARM_DATE_TIME_MAX_HOURS,
  SITE_DISARM_FIXED_DURATION_MAX_MINUTES,
  SITE_DISARM_FIXED_DURATION_OPTIONS,
  SITE_DISARM_FIXED_DURATION_PRESET_MINUTES,
  SITE_DISARM_FOR_OPTIONS,
  createDefaultSiteDisarmUntilAt,
  parseCustomDisarmDurationMinutes,
  resolveDisplayedDisarmFixedDurationMinutes,
} from './disarm-site-popover.types';

@Component({
  selector: 'eventq-disarm-site-popover',
  imports: [MatIcon, EventqDialogPopover, EventqDatetimePicker, EventqSelect],
  templateUrl: './disarm-site-popover.html',
  styleUrl: './disarm-site-popover.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventqDisarmSitePopover {
  readonly open = input(false);
  readonly siteName = input.required<string>();
  readonly reasons = input.required<SiteDisarmReasonOption[]>();
  readonly reasonId = input<string | null>(null);
  readonly disarmFor = input<SiteDisarmForOption>('until-next-schedule');
  readonly fixedDurationMinutes = input<SiteDisarmFixedDurationMinutes | null>(null);
  readonly fixedDurationCustom = input(false);
  readonly fixedDurationCustomDraft = input('');
  /** ISO timestamp for Date & Time mode. */
  readonly untilAt = input<string | null>(null);
  /** IANA zone for the Date & Time calendar. Blank keeps the machine clock. */
  readonly ianaTimeZone = input('');
  readonly timezoneLabel = input('Pacific Daylight Time');
  readonly submitDisabled = input(false);
  readonly cancelLabel = input('Cancel');
  readonly confirmLabel = input('Disarm Site');
  readonly title = input('Disarm Site');
  readonly entityFieldLabel = input('Disarm Site:');
  readonly disarmForOptions = input(SITE_DISARM_FOR_OPTIONS);
  readonly fixedDurationOptions = input(SITE_DISARM_FIXED_DURATION_OPTIONS);
  readonly anchor = input<HTMLElement | null>(null);
  readonly placement = input<EventqDialogPopoverPlacement | 'auto'>('auto');
  readonly panelWidth = input<string | null>('560px');
  /** Positioning for the Disarm Reason dropdown (portaled over overflowing popover content). */
  readonly reasonMenuPlacement = input<EventqSelectMenuPlacement>('bottom-start');
  readonly untilNextScheduleWarning = input('');

  readonly closed = output<void>();
  readonly confirm = output<DisarmSiteConfirm>();
  readonly reasonIdChange = output<string | null>();
  readonly disarmForChange = output<SiteDisarmForOption>();
  readonly fixedDurationMinutesChange = output<SiteDisarmFixedDurationMinutes | null>();
  readonly fixedDurationCustomChange = output<boolean>();
  readonly fixedDurationCustomDraftChange = output<string>();
  readonly untilAtChange = output<string | null>();

  /** Synchronous bounds for Date & Time mode (must not rely on an effect — confirm uses these immediately). */
  readonly dateTimeBounds = computed((): { min: Date; max: Date } | null => {
    if (!this.open() || this.disarmFor() !== 'date-time') {
      return null;
    }

    const min = new Date();
    const max = new Date(min.getTime() + SITE_DISARM_DATE_TIME_MAX_HOURS * 60 * 60 * 1000);
    return { min, max };
  });

  readonly resolvedPlacement = computed((): EventqDialogPopoverPlacement => {
    const placement = this.placement();
    if (placement !== 'auto') {
      return placement;
    }

    return this.anchor() ? 'anchor-top-end' : 'center';
  });

  readonly untilAtDate = computed((): Date | null => {
    const raw = this.untilAt();
    if (!raw) {
      return null;
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  });

  /** Display/confirm value: parent `untilAt`, else now + 1h within bounds when Date & Time is active. */
  readonly effectiveUntilAtDate = computed((): Date | null => {
    const explicit = this.untilAtDate();
    if (explicit) {
      return explicit;
    }

    const bounds = this.dateTimeBounds();
    if (!bounds) {
      return null;
    }

    const fallback = new Date(
      bounds.min.getTime() + SITE_DISARM_DATE_TIME_DEFAULT_OFFSET_HOURS * 60 * 60 * 1000,
    );
    const clamped = Math.min(
      Math.max(fallback.getTime(), bounds.min.getTime()),
      bounds.max.getTime(),
    );
    return new Date(clamped);
  });

  readonly dateTimeMin = computed(() => this.dateTimeBounds()?.min ?? null);
  readonly dateTimeMax = computed(() => this.dateTimeBounds()?.max ?? null);

  /** Banner copy: the site zone name when an IANA zone is set, otherwise `timezoneLabel`. */
  readonly resolvedTimezoneLabel = computed(() => {
    const zone = this.ianaTimeZone().trim();
    if (!zone) {
      return this.timezoneLabel();
    }

    try {
      const name =
        new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'long' })
          .formatToParts(new Date())
          .find((part) => part.type === 'timeZoneName')?.value ?? '';
      return name || this.timezoneLabel();
    } catch {
      return this.timezoneLabel();
    }
  });

  readonly isEffectiveUntilAtInRange = computed((): boolean => {
    const until = this.effectiveUntilAtDate();
    const bounds = this.dateTimeBounds();
    if (!until || !bounds) {
      return false;
    }

    const untilMs = until.getTime();
    return untilMs >= bounds.min.getTime() && untilMs <= bounds.max.getTime();
  });

  readonly confirmDisabled = computed(() => {
    if (this.submitDisabled() || !this.reasonId()) {
      return true;
    }

    const mode = this.disarmFor();
    if (mode === 'until-next-schedule') {
      return false;
    }
    if (mode === 'fixed-time') {
      return this.fixedDurationMinutes() == null;
    }
    if (mode === 'date-time') {
      return !this.isEffectiveUntilAtInRange();
    }
    return true;
  });

  onReasonSelect(reasonId: string): void {
    this.reasonIdChange.emit(reasonId);
  }

  onDisarmForSelect(option: SiteDisarmForOption): void {
    this.disarmForChange.emit(option);
    if (option === 'date-time') {
      this.untilAtChange.emit(this.untilAt() ?? createDefaultSiteDisarmUntilAt());
    }
  }

  onFixedDurationSelect(minutes: SiteDisarmFixedDurationMinutes): void {
    this.fixedDurationCustomChange.emit(false);
    this.fixedDurationCustomDraftChange.emit('');
    this.fixedDurationMinutesChange.emit(minutes);
  }

  onCustomDurationSelect(): void {
    this.fixedDurationCustomChange.emit(true);
    this.fixedDurationCustomDraftChange.emit('');
    this.fixedDurationMinutesChange.emit(null);
  }

  onCustomDurationInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.fixedDurationCustomDraftChange.emit(value);
    this.fixedDurationMinutesChange.emit(parseCustomDisarmDurationMinutes(value));
  }

  onUntilAtApplied(date: Date): void {
    this.untilAtChange.emit(date.toISOString());
  }

  onConfirm(): void {
    const reasonId = this.reasonId();
    if (!reasonId || this.confirmDisabled()) {
      return;
    }

    const disarmFor = this.disarmFor();
    if (disarmFor === 'fixed-time') {
      const durationMinutes = this.fixedDurationMinutes();
      if (durationMinutes == null) {
        return;
      }
      this.confirm.emit({ reasonId, disarmFor, durationMinutes });
      return;
    }

    if (disarmFor === 'date-time') {
      const untilAt = this.untilAt() ?? this.effectiveUntilAtDate()?.toISOString();
      if (!untilAt) {
        return;
      }
      this.confirm.emit({ reasonId, disarmFor, untilAt });
      return;
    }

    this.confirm.emit({ reasonId, disarmFor });
  }
}
